import React, { useState, useEffect, useCallback } from "react";
import { useAuth } from "../../context/AuthContext";

// Configuration groups
const CONFIG_GROUPS = [
  {
    id: "notifiche",
    title: "Notifiche",
    icon: "🔔",
    description: "Impostazioni notifiche email e Teams",
    fields: [
      {
        key: "notifiche_email_attive",
        label: "Notifiche Email Attive",
        type: "boolean",
      },
      {
        key: "notifiche_teams_attive",
        label: "Notifiche Teams Attive",
        type: "boolean",
      },
      {
        key: "notifiche_giorni_anticipo_scadenza",
        label: "Giorni Anticipo Scadenza",
        type: "number",
        min: 1,
        max: 30,
      },
      {
        key: "notifiche_digest_giornaliero",
        label: "Digest Giornaliero",
        type: "boolean",
      },
      {
        key: "notifiche_digest_settimanale",
        label: "Digest Settimanale",
        type: "boolean",
      },
      { key: "notifiche_ora_digest", label: "Ora Invio Digest", type: "time" },
      {
        key: "notifiche_max_giorni_scaduto",
        label: "Stop promemoria scaduti dopo (giorni)",
        type: "number",
        min: 7,
        max: 365,
        help: "Le scadenze scadute da più di questi giorni non generano più promemoria ai fornitori. Default: 90",
      },
    ],
  },
  {
    id: "scadenze",
    title: "Scadenze",
    icon: "📅",
    description: "Impostazioni visualizzazione scadenze",
    fields: [
      {
        key: "scadenze_default_preavviso_giorni",
        label: "Preavviso Default (giorni)",
        type: "number",
        min: 1,
        max: 90,
      },
      {
        key: "scadenze_colore_urgente_giorni",
        label: "Soglia Urgente (giorni)",
        type: "number",
        min: 1,
        max: 14,
      },
      {
        key: "scadenze_colore_warning_giorni",
        label: "Soglia Warning (giorni)",
        type: "number",
        min: 1,
        max: 30,
      },
    ],
  },
  {
    id: "documenti",
    title: "Documenti",
    icon: "📁",
    description: "Impostazioni upload documenti",
    fields: [
      {
        key: "documenti_max_size_mb",
        label: "Dimensione Max (MB)",
        type: "number",
        min: 1,
        max: 100,
      },
      {
        key: "documenti_tipi_ammessi",
        label: "Estensioni Ammesse",
        type: "text",
        placeholder: "pdf,doc,docx,xls,xlsx",
      },
    ],
  },
  {
    id: "calendario",
    title: "Calendario",
    icon: "📆",
    description: "Impostazioni integrazione calendario",
    fields: [
      {
        key: "calendario_sincronizza_automaticamente",
        label: "Sincronizzazione Automatica",
        type: "boolean",
      },
      {
        key: "calendario_default_durata_evento_minuti",
        label: "Durata Evento Default (min)",
        type: "number",
        min: 15,
        max: 480,
      },
      {
        key: "calendario_promemoria_default_minuti",
        label: "Promemoria Default (min)",
        type: "number",
        min: 5,
        max: 60,
      },
    ],
  },
  {
    id: "sistema",
    title: "Sistema",
    icon: "⚙️",
    description: "Impostazioni generali del sistema",
    fields: [
      {
        key: "sistema_lingua",
        label: "Lingua",
        type: "select",
        options: [
          { value: "it", label: "Italiano" },
          { value: "en", label: "English" },
        ],
      },
      {
        key: "sistema_timezone",
        label: "Fuso Orario",
        type: "select",
        options: [{ value: "Europe/Rome", label: "Europe/Rome (CET)" }],
      },
      {
        key: "sistema_formato_data",
        label: "Formato Data",
        type: "select",
        options: [
          { value: "DD/MM/YYYY", label: "DD/MM/YYYY" },
          { value: "YYYY-MM-DD", label: "YYYY-MM-DD" },
        ],
      },
    ],
  },
  {
    id: "report",
    title: "Report",
    icon: "📊",
    description: "Impostazioni report e stampe",
    fields: [
      {
        key: "report_intestazione",
        label: "Intestazione Report",
        type: "text",
      },
      { key: "report_piede_pagina", label: "Piè di Pagina", type: "text" },
      {
        key: "report_logo_url",
        label: "URL Logo",
        type: "text",
        placeholder: "https://...",
      },
    ],
  },
];

function ConfigurazioniPage() {
  const { apiRequest } = useAuth();
  const [config, setConfig] = useState({});
  const [defaults, setDefaults] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [activeGroup, setActiveGroup] = useState("notifiche");
  const [systemInfo, setSystemInfo] = useState(null);
  const [hasChanges, setHasChanges] = useState(false);
  const [originalConfig, setOriginalConfig] = useState({});
  const [schedulerToggling, setSchedulerToggling] = useState(false);
  const [schedulerRunning, setSchedulerRunning] = useState(false);

  // Fetch configuration
  const fetchConfig = useCallback(async () => {
    try {
      const result = await apiRequest("/configurazioni");
      setConfig(result.value || {});
      setOriginalConfig(result.value || {});
      setDefaults(result.defaults || {});
    } catch (err) {
      setError("Errore nel caricamento delle configurazioni");
      console.error("Error fetching config:", err);
    } finally {
      setLoading(false);
    }
  }, [apiRequest]);

  // Fetch system info
  const fetchSystemInfo = useCallback(async () => {
    try {
      const result = await apiRequest("/configurazioni/sistema/info");
      setSystemInfo(result);
    } catch (err) {
      console.error("Error fetching system info:", err);
    }
  }, [apiRequest]);

  useEffect(() => {
    fetchConfig();
    fetchSystemInfo();
  }, [fetchConfig, fetchSystemInfo]);

  // Toggle scheduler
  const toggleScheduler = async (enabled) => {
    setSchedulerToggling(true);
    setError(null);
    setSuccess(null);

    try {
      const result = await apiRequest("/configurazioni/scheduler/toggle", {
        method: "POST",
        body: JSON.stringify({ enabled }),
      });

      setSystemInfo((prev) => ({
        ...prev,
        scheduler: result.scheduler,
      }));

      setSuccess(enabled ? "Scheduler attivato" : "Scheduler disattivato");
    } catch (err) {
      setError(err.message || "Errore durante la modifica dello scheduler");
    } finally {
      setSchedulerToggling(false);
    }
  };

  // Run scheduler manually
  const runSchedulerNow = async () => {
    setSchedulerRunning(true);
    setError(null);
    setSuccess(null);

    try {
      const result = await apiRequest("/configurazioni/scheduler/run", {
        method: "POST",
      });

      setSuccess(
        `Scheduler eseguito: ${result.result?.sent || 0} notifiche inviate`,
      );
    } catch (err) {
      setError(err.message || "Errore durante l'esecuzione dello scheduler");
    } finally {
      setSchedulerRunning(false);
    }
  };

  // Check for changes
  useEffect(() => {
    setHasChanges(JSON.stringify(config) !== JSON.stringify(originalConfig));
  }, [config, originalConfig]);

  // Handle field change
  const handleChange = (key, value) => {
    setConfig((prev) => ({ ...prev, [key]: value }));
    setError(null);
    setSuccess(null);
  };

  // Save configuration
  const handleSave = async () => {
    setSaving(true);
    setError(null);
    setSuccess(null);

    try {
      // Only send changed values
      const changes = {};
      for (const key of Object.keys(config)) {
        if (config[key] !== originalConfig[key]) {
          changes[key] = config[key];
        }
      }

      if (Object.keys(changes).length === 0) {
        setSuccess("Nessuna modifica da salvare");
        setSaving(false);
        return;
      }

      await apiRequest("/configurazioni", {
        method: "PUT",
        body: JSON.stringify(changes),
      });

      setOriginalConfig({ ...config });
      setSuccess("Configurazioni salvate con successo");
    } catch (err) {
      setError(err.message || "Errore durante il salvataggio");
    } finally {
      setSaving(false);
    }
  };

  // Reset to defaults
  const handleResetAll = async () => {
    if (
      !confirm(
        "Sei sicuro di voler ripristinare tutte le configurazioni ai valori predefiniti?",
      )
    ) {
      return;
    }

    setSaving(true);
    setError(null);

    try {
      await apiRequest("/configurazioni/reset", {
        method: "POST",
        body: JSON.stringify({ confirm: true }),
      });

      setConfig({ ...defaults });
      setOriginalConfig({ ...defaults });
      setSuccess("Configurazioni ripristinate ai valori predefiniti");
    } catch (err) {
      setError(err.message || "Errore durante il ripristino");
    } finally {
      setSaving(false);
    }
  };

  // Reset single field
  const handleResetField = (key) => {
    if (defaults[key] !== undefined) {
      handleChange(key, defaults[key]);
    }
  };

  // Render field input
  const renderField = (field) => {
    const value = config[field.key];
    const defaultValue = defaults[field.key];
    const isModified = value !== defaultValue;

    switch (field.type) {
      case "boolean":
        return (
          <div className="flex items-center justify-between">
            <span className="text-gray-700">{field.label}</span>
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={value || false}
                onChange={(e) => handleChange(field.key, e.target.checked)}
                className="sr-only peer"
              />
              <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary-600"></div>
            </label>
          </div>
        );

      case "number":
        return (
          <div>
            <div className="flex items-center justify-between gap-4">
              <label className="text-gray-700">{field.label}</label>
              <div className="flex items-center gap-2">
                <input
                  type="number"
                  value={value ?? ""}
                  onChange={(e) =>
                    handleChange(field.key, parseInt(e.target.value) || 0)
                  }
                  min={field.min}
                  max={field.max}
                  className="input w-24 text-right"
                />
                {isModified && (
                  <button
                    onClick={() => handleResetField(field.key)}
                    className="text-gray-400 hover:text-gray-600"
                    title="Ripristina valore predefinito"
                  >
                    ↺
                  </button>
                )}
              </div>
            </div>
            {field.help && (
              <p className="text-xs text-gray-400 mt-1">{field.help}</p>
            )}
          </div>
        );

      case "time":
        return (
          <div className="flex items-center justify-between gap-4">
            <label className="text-gray-700">{field.label}</label>
            <div className="flex items-center gap-2">
              <input
                type="time"
                value={value || ""}
                onChange={(e) => handleChange(field.key, e.target.value)}
                className="input"
              />
              {isModified && (
                <button
                  onClick={() => handleResetField(field.key)}
                  className="text-gray-400 hover:text-gray-600"
                  title="Ripristina valore predefinito"
                >
                  ↺
                </button>
              )}
            </div>
          </div>
        );

      case "select":
        return (
          <div className="flex items-center justify-between gap-4">
            <label className="text-gray-700">{field.label}</label>
            <div className="flex items-center gap-2">
              <select
                value={value || ""}
                onChange={(e) => handleChange(field.key, e.target.value)}
                className="input"
              >
                {field.options.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
              {isModified && (
                <button
                  onClick={() => handleResetField(field.key)}
                  className="text-gray-400 hover:text-gray-600"
                  title="Ripristina valore predefinito"
                >
                  ↺
                </button>
              )}
            </div>
          </div>
        );

      case "text":
      default:
        return (
          <div className="space-y-1">
            <label className="text-gray-700">{field.label}</label>
            <div className="flex items-center gap-2">
              <input
                type="text"
                value={value || ""}
                onChange={(e) => handleChange(field.key, e.target.value)}
                placeholder={field.placeholder}
                className="input w-full"
              />
              {isModified && (
                <button
                  onClick={() => handleResetField(field.key)}
                  className="text-gray-400 hover:text-gray-600"
                  title="Ripristina valore predefinito"
                >
                  ↺
                </button>
              )}
            </div>
          </div>
        );
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner w-10 h-10"></div>
      </div>
    );
  }

  const activeGroupData = CONFIG_GROUPS.find((g) => g.id === activeGroup);

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">
            Configurazioni Sistema
          </h1>
          <p className="text-gray-500 mt-1">
            Gestisci le impostazioni del portale
          </p>
        </div>
        <div className="flex gap-3">
          <button
            onClick={handleResetAll}
            className="btn-secondary"
            disabled={saving}
          >
            Ripristina Default
          </button>
          <button
            onClick={handleSave}
            className={`btn-primary ${hasChanges ? "animate-pulse" : ""}`}
            disabled={saving || !hasChanges}
          >
            {saving ? "Salvataggio..." : "Salva Modifiche"}
          </button>
        </div>
      </div>

      {/* Messages */}
      {error && (
        <div className="bg-red-50 text-red-600 p-4 rounded-lg">{error}</div>
      )}
      {success && (
        <div className="bg-green-50 text-green-600 p-4 rounded-lg">
          {success}
        </div>
      )}

      <div className="flex gap-6">
        {/* Sidebar */}
        <div className="w-64 flex-shrink-0">
          <nav className="space-y-1">
            {CONFIG_GROUPS.map((group) => (
              <button
                key={group.id}
                onClick={() => setActiveGroup(group.id)}
                className={`w-full text-left px-4 py-3 rounded-lg flex items-center gap-3 transition-colors ${
                  activeGroup === group.id
                    ? "bg-primary-50 text-primary-700 border-l-4 border-primary-600"
                    : "hover:bg-gray-50 text-gray-600"
                }`}
              >
                <span className="text-xl">{group.icon}</span>
                <span className="font-medium">{group.title}</span>
              </button>
            ))}

            {/* System Info */}
            <button
              onClick={() => setActiveGroup("system_info")}
              className={`w-full text-left px-4 py-3 rounded-lg flex items-center gap-3 transition-colors ${
                activeGroup === "system_info"
                  ? "bg-primary-50 text-primary-700 border-l-4 border-primary-600"
                  : "hover:bg-gray-50 text-gray-600"
              }`}
            >
              <span className="text-xl">ℹ️</span>
              <span className="font-medium">Info Sistema</span>
            </button>
          </nav>
        </div>

        {/* Content */}
        <div className="flex-1">
          {activeGroup === "system_info" ? (
            <div className="card">
              <h2 className="text-lg font-semibold mb-4">
                Informazioni Sistema
              </h2>
              {systemInfo ? (
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="bg-gray-50 p-4 rounded-lg">
                      <div className="text-sm text-gray-500">Versione</div>
                      <div className="font-medium">{systemInfo.versione}</div>
                    </div>
                    <div className="bg-gray-50 p-4 rounded-lg">
                      <div className="text-sm text-gray-500">Ambiente</div>
                      <div className="font-medium capitalize">
                        {systemInfo.ambiente}
                      </div>
                    </div>
                    <div className="bg-gray-50 p-4 rounded-lg">
                      <div className="text-sm text-gray-500">Node.js</div>
                      <div className="font-medium">{systemInfo.node}</div>
                    </div>
                    <div className="bg-gray-50 p-4 rounded-lg">
                      <div className="text-sm text-gray-500">Uptime</div>
                      <div className="font-medium">
                        {Math.floor(systemInfo.uptime / 3600)}h{" "}
                        {Math.floor((systemInfo.uptime % 3600) / 60)}m
                      </div>
                    </div>
                  </div>

                  <h3 className="font-medium mt-6 mb-2">Integrazioni</h3>
                  <div className="space-y-2">
                    <div className="flex justify-between items-center py-2 border-b">
                      <span>Dataverse</span>
                      <span
                        className={`px-2 py-1 rounded text-xs ${systemInfo.dataverse?.connected ? "bg-green-100 text-green-700" : "bg-red-100 text-red-700"}`}
                      >
                        {systemInfo.dataverse?.url || "Non configurato"}
                      </span>
                    </div>
                    <div className="flex justify-between items-center py-2 border-b">
                      <span>Microsoft Graph</span>
                      <span
                        className={`px-2 py-1 rounded text-xs ${systemInfo.graph?.configured ? "bg-green-100 text-green-700" : "bg-yellow-100 text-yellow-700"}`}
                      >
                        {systemInfo.graph?.configured
                          ? "Configurato"
                          : "Non configurato"}
                      </span>
                    </div>
                    <div className="flex justify-between items-center py-2 border-b">
                      <span>SharePoint</span>
                      <span
                        className={`px-2 py-1 rounded text-xs ${systemInfo.sharepoint?.configured ? "bg-green-100 text-green-700" : "bg-yellow-100 text-yellow-700"}`}
                      >
                        {systemInfo.sharepoint?.configured
                          ? "Configurato"
                          : "Non configurato"}
                      </span>
                    </div>
                    <div className="flex justify-between items-center py-2">
                      <span>Scheduler</span>
                      <div className="flex items-center gap-2">
                        <span
                          className={`px-2 py-1 rounded text-xs ${systemInfo.scheduler?.enabled ? "bg-green-100 text-green-700" : "bg-gray-100 text-gray-700"}`}
                        >
                          {systemInfo.scheduler?.enabled
                            ? "Attivo"
                            : "Disattivo"}
                        </span>
                        <label className="relative inline-flex items-center cursor-pointer">
                          <input
                            type="checkbox"
                            checked={systemInfo.scheduler?.enabled || false}
                            onChange={(e) => toggleScheduler(e.target.checked)}
                            disabled={schedulerToggling}
                            className="sr-only peer"
                          />
                          <div className="w-9 h-5 bg-gray-200 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-primary-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-primary-600"></div>
                        </label>
                      </div>
                    </div>

                    {/* Manual scheduler run */}
                    <div className="mt-4 pt-4 border-t">
                      <div className="flex items-center justify-between">
                        <div>
                          <div className="font-medium text-gray-700">
                            Esecuzione Manuale
                          </div>
                          <div className="text-xs text-gray-500">
                            Esegui il controllo notifiche ora
                          </div>
                        </div>
                        <button
                          onClick={runSchedulerNow}
                          disabled={schedulerRunning}
                          className="btn-secondary text-sm flex items-center gap-2"
                        >
                          {schedulerRunning ? (
                            <>
                              <div className="spinner w-4 h-4"></div>
                              Esecuzione...
                            </>
                          ) : (
                            <>▶️ Esegui Ora</>
                          )}
                        </button>
                      </div>
                    </div>
                  </div>

                  <h3 className="font-medium mt-6 mb-2">Memoria</h3>
                  {systemInfo.memoria && (
                    <div className="text-sm text-gray-600">
                      <div>
                        Heap Used:{" "}
                        {Math.round(systemInfo.memoria.heapUsed / 1024 / 1024)}{" "}
                        MB
                      </div>
                      <div>
                        Heap Total:{" "}
                        {Math.round(systemInfo.memoria.heapTotal / 1024 / 1024)}{" "}
                        MB
                      </div>
                      <div>
                        RSS: {Math.round(systemInfo.memoria.rss / 1024 / 1024)}{" "}
                        MB
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <p className="text-gray-500">Caricamento informazioni...</p>
              )}
            </div>
          ) : (
            activeGroupData && (
              <div className="card">
                <div className="flex items-center gap-3 mb-6">
                  <span className="text-2xl">{activeGroupData.icon}</span>
                  <div>
                    <h2 className="text-lg font-semibold">
                      {activeGroupData.title}
                    </h2>
                    <p className="text-sm text-gray-500">
                      {activeGroupData.description}
                    </p>
                  </div>
                </div>

                <div className="space-y-6">
                  {activeGroupData.fields.map((field) => (
                    <div
                      key={field.key}
                      className="pb-4 border-b last:border-0"
                    >
                      {renderField(field)}
                    </div>
                  ))}
                </div>
              </div>
            )
          )}
        </div>
      </div>
    </div>
  );
}

export default ConfigurazioniPage;
